import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { priorityColor } from "@/lib/api";
import { incidentMarkerHtml } from "@/lib/branchIcons";
import { tileUrl, tileAttribution, TILE_MAX_ZOOM } from "@/lib/mapTiles";
import { useTheme } from "@/contexts/ThemeContext";

function makeIcon(branch, priority, selected) {
  return L.divIcon({
    html: incidentMarkerHtml(branch, priorityColor(priority), selected),
    className: "",
    iconSize: [38, 46],
    iconAnchor: [19, 45],   // tip of the pin
    popupAnchor: [0, -40],
  });
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

// Emit a `click` event when the dispatcher clicks empty map (not a marker).
// Used for reverse-geocode pin drop → auto-fill the CAD address form.
function PinDropListener({ onClick }) {
  useMapEvents({
    click(e) {
      if (!onClick) return;
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
    const tt = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${dd} ${tt}`;
  } catch { return iso; }
}

function IncidentPopup({ incident, onOpenDetail, onOpenUpdate }) {
  const branch = (incident.branch || "POLICE").replace("PD", "POLICE").toUpperCase();
  const priority = ((incident.priority === "critical" ? "high" : incident.priority) || "low").toUpperCase();
  const allUpdates = incident.updates || [];
  // Only the SINGLE most recent user-relevant update (notes / call-type changes / paged / note).
  // "created" is intentionally excluded because the header already conveys it.
  const meaningful = allUpdates.filter((u) => ["notes_changed", "note", "call_type_changed", "paged"].includes(u.type));
  const latest = meaningful.length ? meaningful[meaningful.length - 1] : null;
  const hasMore = meaningful.length > 1;

  // Deduped list of unit ids that paged or updated this call.
  const unitSet = new Set();
  for (const u of allUpdates) {
    if (u.unit_id) unitSet.add(u.unit_id);
  }
  const units = Array.from(unitSet);

  const latestText = latest
    ? (latest.type === "notes_changed" ? (incident.notes || "") : (latest.message || ""))
    : (incident.notes || "");

  return (
    <div className="font-mono text-xs space-y-1" style={{ minWidth: 260, maxWidth: 320 }}>
      {/* Incident # */}
      <div className="flex items-center justify-between gap-2">
        <div className="font-bold text-primary" data-testid="popup-incident-number">{incident.incident_number || "—"}</div>
        <div className="flex items-center gap-1">
          <span className="uppercase text-[10px] px-1 rounded-sm border border-border">{branch}</span>
          <span className="uppercase text-[10px] px-1 rounded-sm" style={{ color: priorityColor(priority.toLowerCase()) }}>{priority}</span>
        </div>
      </div>

      {/* Call Type */}
      <div className="font-bold text-sm" data-testid="popup-call-type">{incident.call_type || "—"}</div>

      {/* Incident Information (formatted line — order per spec:
          STATE | CITY | CALL TYPE | BOX # | ADDRESS | CALL INFO/UPDATE) */}
      <div className="border border-black/20 rounded-sm p-1.5 bg-white text-black leading-snug">
        <div className="text-[10px] text-black/70 tracking-widest mb-0.5">INCIDENT INFORMATION</div>
        <div className="break-words" data-testid="popup-incident-info">
          <span>{incident.state || "—"}</span>
          <span className="text-black/50"> | </span>
          <span>{incident.city || "—"}</span>
          <span className="text-black/50"> | </span>
          <span className="font-semibold">{incident.call_type || "—"}</span>
          <span className="text-black/50"> | </span>
          <span>{incident.box_number ? `Box ${incident.box_number}` : "—"}</span>
          <span className="text-black/50"> | </span>
          <span>{incident.address || "—"}</span>
          <span className="text-black/50"> | </span>
          <span className="text-primary">{latestText || "—"}</span>
          {incident.page_number != null && (
            <>
              <span className="text-black/50"> | </span>
              <span className="text-amber-600">Page #{incident.page_number}</span>
            </>
          )}
        </div>
      </div>

      {/* Latest update only */}
      {latestText && (
        <div className="border-l-2 border-primary/40 pl-2">
          <div className="text-[10px] text-muted-foreground">
            {latest ? fmtDT(latest.timestamp) : fmtDT(incident.updated_at)}
            {" – "}
            <span className="text-primary">{(latest && latest.unit_id) || "—"}</span>
          </div>
          <div className="leading-snug" data-testid="popup-latest-update">{latestText}</div>
        </div>
      )}

      {/* Dispatcher / Units */}
      {units.length > 0 && (
        <div className="pt-1 border-t border-border/60">
          <div className="text-[10px] text-muted-foreground tracking-widest mb-0.5">DISPATCHERS</div>
          <div className="font-mono text-[11px]" data-testid="popup-units">{units.join(", ")}</div>
        </div>
      )}

      {/* Show More → Incident Detail, UPDATE → Update form. Split buttons so
          dispatchers can jump straight into an update without loading the
          full detail dialog first. */}
      {(onOpenDetail || onOpenUpdate) && (
        <div className="flex gap-1 mt-1">
          {onOpenDetail && (hasMore || (incident.updates || []).length > 0) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenDetail(incident); }}
              data-testid="popup-show-more-btn"
              className="flex-1 text-center text-[10px] tracking-widest font-mono uppercase py-1 border border-border rounded-sm hover:bg-accent"
            >
              Show more →
            </button>
          )}
          {onOpenUpdate && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenUpdate(incident); }}
              data-testid="popup-update-btn"
              className="flex-1 text-center text-[10px] tracking-widest font-mono uppercase py-1 border rounded-sm bg-destructive text-destructive-foreground border-destructive hover:opacity-90"
            >
              Update
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function IncidentMap({ incidents = [], center, selectedId, onSelect, onOpenDetail, onOpenUpdate, onMapClick, height = "100%", flyZoom = 14 }) {
  const { theme } = useTheme();
  const mapRef = useRef();

  const initialCenter = center || [39.8283, -98.5795];
  const initialZoom = center ? 12 : 4;

  const markers = useMemo(() => incidents.filter((i) => i.lat && i.lon), [incidents]);

  return (
    <MapContainer
      center={initialCenter}
      zoom={initialZoom}
      style={{ height, width: "100%" }}
      ref={mapRef}
      zoomControl={true}
      scrollWheelZoom={true}
    >
      <TileLayer
        key={theme}
        url={tileUrl(theme)}
        attribution={tileAttribution()}
        maxZoom={TILE_MAX_ZOOM}
      />
      <FlyTo center={center} zoom={flyZoom} />
      {onMapClick && <PinDropListener onClick={onMapClick} />}

      {markers.map((i) => (
        <Marker
          key={i.id}
          position={[i.lat, i.lon]}
          icon={makeIcon(i.branch, i.priority, selectedId === i.id)}
          eventHandlers={{ click: () => onSelect?.(i) }}
        >
          <Popup>
            <IncidentPopup incident={i} onOpenDetail={onOpenDetail} onOpenUpdate={onOpenUpdate} />
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
